import React, { useState, useEffect, useCallback, memo } from 'react';
import { Box, Typography, TextField, Button, IconButton, CircularProgress, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles, getInputStyles } from './styles';
import { validateAwards } from './validation';
import { MONTHS, YEAR_RANGE, parseDateString } from './constants';

const AwardsSection = memo(({ initialData, authAxios, setError, onUpdate }) => {
    const [awards, setAwards] = useState(initialData || []);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(true);

    useEffect(() => {
        if (initialData) {
            // Parse dates from API (YYYY-MM-DD) to month/year components
            const parsedData = initialData.map(award => {
                const dateParsed = parseDateString(award.date_received);
                return {
                    ...award,
                    received_month: dateParsed.month,
                    received_year: dateParsed.year,
                };
            });
            setAwards(parsedData);
        }
    }, [initialData]);

    const addAward = useCallback(() => {
        setAwards(prev => [...prev, { id: Date.now(), name: '', issuer: '', received_month: '', received_year: '', description: '' }]);
    }, []);

    const updateAward = useCallback((id, field, value) => {
        setAwards(prev => prev.map(award => award.id === id ? { ...award, [field]: value } : award));
    }, []);

    const removeAward = useCallback(async (id) => {
        if (typeof id === 'number' && id > 1000000000000) {
            setAwards(prev => prev.filter(award => award.id !== id));
            return;
        }
        try {
            await authAxios.delete(`/awards/${id}/`);
            setAwards(prev => prev.filter(award => award.id !== id));
        } catch (error) {
            setError?.('Failed to remove award');
        }
    }, [authAxios, setError]);

    // Format date for API (YYYY-MM-DD)
    const formatDate = (month, year) => {
        if (!month || !year) return null;
        return `${year}-${month.padStart(2, '0')}-01`;
    };

    const handleSaveAll = async () => {
        const errors = validateAwards(awards);
        if (errors.length > 0) {
            setShowErrors(true);
            setError?.('Please fill in required fields for all awards');
            return;
        }

        setSaving(true);
        try {
            const savedEntries = await Promise.all(awards.map(async award => {
                const payload = { 
                    name: award.name, 
                    issuer: award.issuer, 
                    date_received: formatDate(award.received_month, award.received_year),
                    description: award.description 
                };
                if (typeof award.id === 'number' && award.id > 1000000000000) {
                    return (await authAxios.post('/awards/', payload)).data;
                }
                return (await authAxios.patch(`/awards/${award.id}/`, payload)).data;
            }));
            // Parse dates from response
            const parsedEntries = savedEntries.map(award => {
                const dateParsed = parseDateString(award.date_received);
                return {
                    ...award,
                    received_month: dateParsed.month,
                    received_year: dateParsed.year,
                };
            });
            setAwards(parsedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            setError?.('Failed to save awards');
        } finally {
            setSaving(false);
        }
    };

    const hasFieldError = (idx, field) => showErrors && field === 'name' && !awards[idx]?.name?.trim();

    // Dropdown styles
    const selectStyles = {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
        },
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>Awards</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={addAward} startIcon={<AddIcon />} sx={{ color: '#4f46e5', textTransform: 'none', fontWeight: 500 }}>Add Award</Button>
                    <Button variant="contained" onClick={handleSaveAll} disabled={saving || awards.length === 0}
                        sx={{ bgcolor: '#4f46e5', color: 'white', textTransform: 'none', fontWeight: 500, borderRadius: '6px', px: 3, '&:hover': { bgcolor: '#4338ca' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                    </Button>
                </Box>
            </Box>
            {awards.length === 0 ? (
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>No awards added.</Typography>
            ) : awards.map((award, index) => (
                <Box key={award.id} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 3, mb: 2, position: 'relative' }}>
                    <IconButton size="small" onClick={() => removeAward(award.id)} sx={{ position: 'absolute', top: 8, right: 8 }}><CloseIcon fontSize="small" /></IconButton>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Name<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span></Typography>
                            <TextField fullWidth size="small" placeholder="Award name" value={award.name || ''}
                                onChange={(e) => updateAward(award.id, 'name', e.target.value)} sx={hasFieldError(index, 'name') ? getInputStyles(true, true) : inputStyles} />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Issuer</Typography>
                            <TextField fullWidth size="small" placeholder="Issuing organization" value={award.issuer || ''}
                                onChange={(e) => updateAward(award.id, 'issuer', e.target.value)} sx={inputStyles} />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Date Received</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                <Select
                                    size="small"
                                    value={award.received_month || ''}
                                    onChange={(e) => updateAward(award.id, 'received_month', e.target.value)}
                                    displayEmpty
                                    IconComponent={ExpandMoreIcon}
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">Month</MenuItem>
                                    {MONTHS.map(m => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </Select>
                                <Select
                                    size="small"
                                    value={award.received_year || ''}
                                    onChange={(e) => updateAward(award.id, 'received_year', e.target.value)}
                                    displayEmpty
                                    IconComponent={ExpandMoreIcon}
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">Year</MenuItem>
                                    {YEAR_RANGE.map(y => (
                                        <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Description</Typography>
                            <TextField fullWidth size="small" placeholder="Brief description" value={award.description || ''}
                                onChange={(e) => updateAward(award.id, 'description', e.target.value)} sx={inputStyles} />
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );
});

AwardsSection.displayName = 'AwardsSection';
export default AwardsSection;
